import { Injectable, inject } from '@angular/core';

import { UserService } from './user.service';

import { verifyPhoneNumber } from '../constants/constants';
import moment from 'moment';

export interface UploadedUser {
  name: string;
  email: string;
  autodelivery: boolean;
  email2: string;
  sms: string;
  fax: string;
  valid: boolean;
  invalidFields: string[];
}

export interface UserImportAvailability {
  online: boolean;
  app: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class UserAdminImportService {
  private readonly userService = inject(UserService);

  readUploadedCsv(csvContent: string): UploadedUser[] {
    const newUsers: UploadedUser[] = [];
    const separator = csvContent.includes(';') ? ';' : ',';
    const lines = csvContent.split('\n');

    for (let index = 0; index < lines.length; index++) {
      const line = this.normalizeCsvLine(lines[index]);

      if (!line) {
        continue;
      }

      const columns = line.split(separator);

      if (columns.length < 3) {
        continue;
      }

      const [name, email, autodelivery, email2, sms, fax] = columns;

      if (this.isHeaderRow(index, name, email)) {
        continue;
      }

      const hasRequiredFields = name !== '' && email.includes('@');

      newUsers.push({
        name,
        email,
        autodelivery: autodelivery?.toLowerCase() === 'ja',
        email2,
        sms,
        fax,
        valid: hasRequiredFields,
        invalidFields: hasRequiredFields
          ? []
          : this.getBasicInvalidFields(name, email),
      });
    }

    return newUsers;
  }

  async verifyNewUsers(newUsers: UploadedUser[]): Promise<UploadedUser[]> {
    const validatedUsers: UploadedUser[] = [];

    for (const user of newUsers) {
      validatedUsers.push(await this.verifyUploadedUser(user));
    }

    return validatedUsers;
  }

  async verifyUserName(email: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.userService.userAlreadyExists(email).subscribe({
        next: data => resolve(data !== true),
        error: error => reject(error),
      });
    });
  }

  createUserDataToImport(
    user: UploadedUser,
    canBecomeActive: UserImportAvailability,
    deliveryTarget: { email: string[]; sms: string[]; fax: string[] }
  ): any {
    const today = moment().set({
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0,
    });

    const dtgEndless = this.userService.getEndlessDtg();
    const dtgFormat = 'YYYY-MM-DDT00:00:00';

    return {
      customerId: this.userService.customerDocument().customerId,
      groups: ['EINSATZWETTER_USER'],
      email: user.email,
      name: user.name,
      metadata: {
        name: user.name,
        start: today.format(dtgFormat) + 'Z',
        end: dtgEndless,
        active: true,
        online: {
          start: today.format(dtgFormat) + 'Z',
          end: dtgEndless,
          active: canBecomeActive.online,
          product: null,
        },
        delivery: {
          start: today.format(dtgFormat) + 'Z',
          end: dtgEndless,
          active: user.autodelivery,
          product: [],
          target: deliveryTarget,
        },
        app: {
          start: today.format(dtgFormat) + 'Z',
          end: dtgEndless,
          active: false,
          product: null,
        },
      },
    };
  }

  createImportUserDeliveryTargets(
    user: UploadedUser,
    contingents: any,
    usedContingents: any
  ): { email: string[]; sms: string[]; fax: string[] } {
    const target = {
      email: [user.email],
      sms: [],
      fax: [],
    };

    if (user.email2 !== null && user.email2 !== '') {
      target.email.push(user.email2);
    }

    if (
      user.sms !== null &&
      user.sms !== '' &&
      contingents.sms > usedContingents.sms
    ) {
      target.sms = [user.sms];
    }

    if (
      user.fax !== null &&
      user.fax !== '' &&
      contingents.fax > usedContingents.fax
    ) {
      target.fax = [user.fax];
    }

    return target;
  }

  private async verifyUploadedUser(user: UploadedUser): Promise<UploadedUser> {
    const invalidFields = [...user.invalidFields];
    let valid = user.valid;

    if (user.name === '') {
      this.addInvalidField(invalidFields, 'name');
      valid = false;
    }

    if (!user.email.includes('@')) {
      this.addInvalidField(invalidFields, 'emailWrongFormat');
      valid = false;
    } else {
      const userNameAvailable = await this.verifyUserName(user.email);

      if (!userNameAvailable) {
        this.addInvalidField(invalidFields, 'emailAlreadyExist');
        valid = false;
      }
    }

    return {
      ...user,
      email2:
        user.email2 !== '' && !user.email2.includes('@') ? null : user.email2,
      sms: verifyPhoneNumber(user.sms),
      fax: verifyPhoneNumber(user.fax),
      valid,
      invalidFields,
    };
  }

  private normalizeCsvLine(line: string): string {
    return line.replace('\r', '').replace('"', '').replace("'", '').trim();
  }

  private isHeaderRow(index: number, name: string, email: string): boolean {
    return index === 0 && (name === '' || !email.includes('@'));
  }

  private getBasicInvalidFields(name: string, email: string): string[] {
    const invalidFields: string[] = [];

    if (name === '') {
      invalidFields.push('name');
    }

    if (!email.includes('@')) {
      invalidFields.push('emailWrongFormat');
    }

    return invalidFields;
  }

  private addInvalidField(invalidFields: string[], field: string): void {
    if (!invalidFields.includes(field)) {
      invalidFields.push(field);
    }
  }
}
